import { GISSearchResult } from "./gis-search-result";
import LayerSearchSource from "@arcgis/core/widgets/Search/LayerSearchSource";
import MapView from "@arcgis/core/views/MapView";
import Graphic from "@arcgis/core/Graphic";
import { union } from "@arcgis/core/geometry/geometryEngine";
import PopupOpenOptions = __esri.PopupOpenOptions;

export class GISLayerSearchResult {
  results: GISSearchResult[];
  searchTitle: string;
  searchTerm: string;
  isNMCRISShapeLayer: boolean;
  customPopupTitle: boolean;

  constructor(
    results: GISSearchResult[],
    isNMCRISShapeLayer: boolean,
    searchTitle: string,
    searchTerm: string,
    customPopupTitle: boolean,
  ) {
    this.results = results;
    this.searchTitle = searchTitle;
    this.searchTerm = searchTerm;
    this.isNMCRISShapeLayer = isNMCRISShapeLayer;
    this.customPopupTitle = customPopupTitle;
  }

  private generateSearchResultLabel(): string {
    return `<div><small>${this.searchTitle}</small></div>
                <div><strong>${this.searchTerm}</strong></div>`;
  }

  generateSearchResultDiv(
    view: MapView,
    removeResultListener: EventListener,
  ): HTMLDivElement {
    // create a new search entry
    const searchResultDiv = document.createElement("div");
    searchResultDiv.className = "list-group-item flex-1";
    const searchResultHeaderDiv = document.createElement("div");
    searchResultHeaderDiv.className =
      "d-flex mb-2 justify-content-start align-items-center";
    const navigateAllButton = document.createElement("div");
    navigateAllButton.className =
      "flex-shrink-0 esri-icon-navigation esri-widget--button esri-widget esri-interactive esri-shadow mr-2";
    navigateAllButton.title = "Zoom to all shapes";
    navigateAllButton.tabIndex = 0;
    navigateAllButton.setAttribute("role", "button");
    navigateAllButton.onclick = (e) => {
      e.preventDefault();
      this.goToTargets(
        view,
        this.results.map((r) => r.target),
        this.searchTerm,
      );
    };
    const searchResultLabel = document.createElement("div");
    searchResultLabel.className = "mr-auto";
    searchResultLabel.innerHTML = this.generateSearchResultLabel();
    const searchResultDeleteButton = document.createElement("div");
    searchResultDeleteButton.className =
      "flex-shrink-0 esri-icon-close esri-widget--button esri-widget esri-interactive esri-shadow ml-2";
    searchResultDeleteButton.title = "Clear this search result";
    searchResultDeleteButton.tabIndex = 0;
    searchResultDeleteButton.setAttribute("role", "button");
    searchResultDeleteButton.addEventListener("click", removeResultListener);
    searchResultHeaderDiv.append(
      navigateAllButton,
      searchResultLabel,
      searchResultDeleteButton,
    );
    searchResultDiv.appendChild(searchResultHeaderDiv);

    // If there is only 1 "result", then it should match the "top-level" (searchResultHeaderDiv) result.
    // No need to show duplicates.
    if (this.results?.length < 2) {
      return searchResultDiv;
    }

    const subResults = document.createElement("div");
    for (const result of this.results) {
      // build search result shapes list
      const target = result.target;
      const objectId = this.isNMCRISShapeLayer
        ? target.attributes.OBJECTID
        : crypto.randomUUID();
      const label = document.createElement("label");
      label.htmlFor = `object-${objectId}`;
      label.className =
        "list-group-item user-select-none d-flex justify-content-between";
      // this container is so the focus outline only surrounds the checkbox and isn't stretched vertically
      const span = document.createElement("span");
      span.innerText =
        result.label ?? `SHAPE-${objectId.toString(16).toUpperCase()}`;
      span.className = "flex-grow-1";
      span.style.margin = "auto 0";
      const navigateButton = document.createElement("div");
      navigateButton.className =
        "flex-shrink-0 esri-icon-navigation esri-widget--button esri-widget esri-interactive esri-shadow mr-2";
      navigateButton.title = "Zoom to shape";
      navigateButton.tabIndex = 0;
      navigateButton.setAttribute("role", "button");
      navigateButton.onclick = (e) => {
        e.preventDefault();
        this.goToTargets(view, [result.target], result.label ?? "");
      };
      label.append(navigateButton, span);
      subResults.appendChild(label);
    }
    searchResultDiv.appendChild(subResults);
    return searchResultDiv;
  }

  /**
   * Zoom the center of one or more polygons or points. All `targets` should be of the same Geometry type.
   * @param {MapView} view - The MapView within which to zoom to `targets`
   * @param {Graphic[]} targets - One or more targets to zoom to
   * @param {string} popupTitle - Title to use for the Popup when the targets are not of a FeatureLayer
   * @private
   */
  private goToTargets(view: MapView, targets: Graphic[], popupTitle: string) {
    view.goTo(targets).then(async () => {
      let centroid = await union(targets.map((g) => g.geometry)); // If this is a Point, use the Geometry itself.
      if (centroid.extent) {
        // Polygons have an Extent. Points do not.
        centroid = centroid.extent.center;
      }
      let options: PopupOpenOptions = {
        features: targets,
        location: centroid,
      };

      if (this.customPopupTitle) {
        // "unset" the Graphics array. Overriding titles doesn't seem to work otherwise.
        options.features = [];
        options.title = popupTitle;
      }
      view.popup.open(options);
    });
  }
}
